import {
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import React from 'react'

// Modern Layout component with sidebar design
export const Layout: React.FC<{
  banner?: React.ReactNode
  clearFiltersHandler?: () => void
  filters?: React.ReactNode
  isLoading?: boolean
  header?: string
  pagination?: React.ReactNode
  textSearch?: React.ReactNode
  resultsDescription?: React.ReactNode
  sortDropdown?: React.ReactNode
  results?: React.ReactNode
}> = ({
  banner,
  filters,
  isLoading,
  header,
  pagination,
  textSearch,
  resultsDescription,
  sortDropdown,
  results,
}) => {
  return (
    <Box>
      {/* Banner */}
      {banner && <Box sx={{ mb: 3 }}>{banner}</Box>}

      {/* Header */}
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        {header}
      </Typography>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Filters Section - Progressive responsive sizing */}
        <Grid item xs={12} md={4} lg={3}>
          {filters}
        </Grid>

        {/* Main Content Area - Search, Sort, Results */}
        <Grid item xs={12} md={8} lg={9}>
          {/* Search and Sort Section */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Search Bar - Full width until large screens */}
            <Grid item xs={12} lg={7}>
              {textSearch}
            </Grid>

            {/* Sort and Clear Filters - Full width until large screens */}
            <Grid item xs={12} lg={5}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexDirection: { xs: 'column', lg: 'row' },
                  alignItems: { xs: 'stretch', lg: 'center' },
                  justifyContent: { lg: 'flex-end' },
                }}
              >
                {sortDropdown}
              </Box>
            </Grid>
          </Grid>

          {/* Results Description */}
          {resultsDescription && <Box sx={{ mb: 2 }}>{resultsDescription}</Box>}

          {/* Results or Loading */}
          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>Loading...</Typography>
            </Box>
          ) : (
            results
          )}

          {/* Pagination */}
          {pagination && (
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
              {pagination}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}

// Simplified ResultsDescription component
export const ResultsDescription: React.FC<{
  start?: number
  end?: number
  total?: number
  description?: string
  textSearch?: string | string[]
}> = ({ start, end, total, description, textSearch }) => {
  return (
    <Typography variant="body2" sx={{ mb: 2 }}>
      Showing {start}-{end} of {total} {description}
      {textSearch && ` for "${textSearch}"`}
    </Typography>
  )
}

// Simplified SortDropdown component
export const SortDropdown: React.FC<{
  handleonSelectChange?: (value: string) => void
  sortOptions?: Array<{ label: string; value: string }>
  sortValue?: string
}> = ({ handleonSelectChange, sortOptions = [], sortValue }) => {
  return (
    <Select
      value={sortValue || ''}
      onChange={(e) => handleonSelectChange?.(e.target.value)}
      size="small"
      displayEmpty
      fullWidth
      sx={{
        backgroundColor: '#fff',
        minWidth: { xs: 'auto', sm: '180px' },
      }}
    >
      {sortOptions.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  )
}

// Simplified TextSearch component
export const TextSearch: React.FC<{
  label?: string
  defaultValue?: string
  onSubmitHandler?: (
    e: React.FormEvent<HTMLFormElement>,
    textValue: string,
  ) => void
}> = ({ label, defaultValue, onSubmitHandler }) => {
  const [value, setValue] = React.useState(defaultValue || '')

  // Update internal state when defaultValue changes (e.g., when filters are cleared)
  React.useEffect(() => {
    setValue(defaultValue || '')
  }, [defaultValue])

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onSubmitHandler?.(e, value)
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: 'flex', gap: 1, width: '100%' }}
    >
      <TextField
        label={label}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        size="small"
        variant="outlined"
        fullWidth
        sx={{ flex: 1 }}
      />
      <Button
        type="submit"
        variant="contained"
        size="small"
        sx={{
          minWidth: { xs: '80px', sm: 'auto' },
          whiteSpace: 'nowrap',
        }}
      >
        Search
      </Button>
    </Box>
  )
}
